import React from 'react';
import { ChevronRight } from 'lucide-react';
import { isToday, isSameMonth } from 'date-fns';
import { formatDate } from '../utils/dateUtils';

/**
 * Calendar day cell component with truck count indicators
 */
const DayCell = ({ 
  dayInfo, 
  currentMonth, 
  selectedDate, 
  onDateSelect,
  mode = 'inbound'
}) => {
  // If no day data (empty cell at start/end of month), render empty cell
  if (!dayInfo) {
    return <div className="h-auto min-h-[6rem] bg-slate-800 border border-slate-700"></div>;
  }
  
  const { day, date, trucksCount, statusBreakdown, totalPallets } = dayInfo;
  
  // Determine cell classes based on state
  const isCurrentDay = isToday(date);
  const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
  const isCurrentMonth = isSameMonth(date, currentMonth);
  
  // Theme color based on mode
  const themeColor = mode === 'inbound' ? 'blue' : 'green';
  
  // Base cell classes
  let cellClasses = 'h-auto min-h-[6rem] p-1 border relative transition-colors group';
  
  // Add conditional classes
  if (isSelected) {
    cellClasses += ` bg-${themeColor}-700/20 border-${themeColor}-500`;
  } else if (isCurrentDay) {
    cellClasses += ` bg-${themeColor}-900/20 border-${themeColor}-700`;
  } else if (isCurrentMonth) {
    cellClasses += ' bg-slate-800 border-slate-700 hover:bg-slate-750';
  } else {
    cellClasses += ' bg-slate-800/50 border-slate-700/50 text-slate-500 hover:bg-slate-750/50';
  }
  
  return (
    <div 
      className={cellClasses}
      onClick={() => onDateSelect(date)}
    >
      {/* Day number */}
      <div className={`text-right text-white ${isCurrentDay ? `text-${themeColor}-400 font-bold` : ''}`}>
        {day}
      </div>
      
      {/* Truck counts by status */}
      {trucksCount > 0 && (
        <div className="mt-1">
          {statusBreakdown.scheduled > 0 && (
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-300">Scheduled:</span>
              <span className="text-slate-400">{statusBreakdown.scheduled}</span>
            </div>
          )}
          {statusBreakdown.inProgress > 0 && (
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-300">In Progress:</span>
              <span className="text-slate-400">{statusBreakdown.inProgress}</span>
            </div>
          )}
          {statusBreakdown.shipped > 0 && (
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-300">Completed:</span>
              <span className="text-slate-400">{statusBreakdown.shipped}</span>
            </div>
          )}

          {/* Total pallets scheduled for the day (pallet-based loads only) */}
          {totalPallets > 0 && (
            <div className="mt-1 pt-1 border-t border-slate-600/50">
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="text-slate-200">
                  {totalPallets} {totalPallets === 1 ? 'pallet' : 'pallets'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Status count indicators */}
      <div className="mt-1 flex justify-center gap-1">
        {trucksCount > 0 ? (
          <div className="flex items-center gap-1">
            {statusBreakdown.scheduled > 0 && (
              <div className={`w-2 h-2 rounded-full bg-yellow-500`}></div>
            )}
            {statusBreakdown.inProgress > 0 && (
              <div className={`w-2 h-2 rounded-full bg-${themeColor}-500`}></div>
            )}
            {statusBreakdown.shipped > 0 && (
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-600">No trucks</div>
        )}
      </div>
      
      {/* View detail indicator on hover */}
      {trucksCount > 0 && (
        <div className={`absolute inset-0 bg-${themeColor}-800/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity`}>
          <div className="text-white flex items-center text-sm font-medium">
            <span>View Details</span>
            <ChevronRight className="w-4 h-4 ml-1" />
          </div>
        </div>
      )}
    </div>
  );
};

export default DayCell;